import { Button, Heading, LayoutContainer, Picture, TextPassage } from '@petsmart-ui/sparky';
import { ContentfulClick } from '@libs/analytics';
import Link from 'next/link';
import cn from 'classnames';
import type { BrandSpotlightProps } from './types';
import { getImageProps } from '../../../utils/propsImagesItems';
import './BrandSpotlight.scss';

export function BrandSpotlight({ leftBlock, rightBlock }: BrandSpotlightProps) {
    if (rightBlock.length !== 2) {
        return null;
    }

    const [brandCard] = leftBlock;
    const [assetLg, assetSm] = rightBlock;
    const Component = brandCard.cta ? Link : 'div';
    const assets = {
        sm: {
            image: getImageProps(assetSm.image),
            internalTitle: assetSm.internalTitle,
            link: assetSm.link,
        },
        lg: {
            image: getImageProps(assetLg.image),
            internalTitle: assetLg.internalTitle,
            link: assetLg.link,
        },
    };

    return (
        <LayoutContainer className="brand-spotlight">
            {Object.entries(assets).map(([size, { image, link, internalTitle }]) => (
                <ContentfulClick
                    component={Link}
                    key={size}
                    href={link?.webUrl || ''}
                    className={`brand-spotlight__${size}`}
                    eventData={{
                        linkText: link.internalTitle,
                        linkUrl: link?.webUrl ?? '',
                        promoTitle: brandCard.header,
                        promoVariant: 'storytelling card up - Brand Spotlight - image',
                        promoDesc: internalTitle,
                        promoType: `brand-spotlight__${size}`,
                    }}
                >
                    <Picture {...image} />
                </ContentfulClick>
            ))}

            <ContentfulClick
                component={Component}
                className="brand-spotlight__card"
                style={{ background: brandCard.backgroundFill }}
                href={brandCard.cta?.link.webUrl ?? ''}
                eventData={{
                    linkText: cn(brandCard.header, brandCard.bodyText, brandCard.cta?.text),
                    linkUrl: brandCard.cta?.link.webUrl ?? '',
                    promoTitle: brandCard.header,
                    promoVariant: 'storytelling card up - Brand Spotlight - Card',
                    promoDesc: brandCard.internalTitle,
                    promoType: 'brand-spotlight__card',
                }}
                prefetch={false} // https://petsmart.atlassian.net/browse/HCP-594
            >
                <Heading tagName="h2">{brandCard.header}</Heading>
                <TextPassage>{brandCard.bodyText}</TextPassage>

                {brandCard.cta && (
                    <Button
                        size="sm"
                        text={brandCard.cta.text}
                        href={brandCard.cta.link.webUrl}
                        variant={brandCard.cta.buttonStyle === 'Primary' ? 'primary' : 'toolbar'}
                    />
                )}
            </ContentfulClick>
        </LayoutContainer>
    );
}
